import { Cross2Icon } from "@radix-ui/react-icons";
import clsx from "clsx";
import React, { useEffect, useRef, useState } from "react";
import { FormattedMessage } from "react-intl";
import { Link } from "react-router-dom";
import { toast as sonnerToast, Toaster } from "sonner";

import { ZIndex } from "~/common/constants";
import { featureFlags } from "~/common/feature-flags";
import { fetchWithAuth } from "~/common/fetch";
import { remoteLog } from "~/common/logging";
import { useRequiredUser } from "~/common/user";
import { Button, type ButtonProps } from "~/components/ui/button";
import { LoadingButton, type LoadingButtonProps } from "~/components/ui/loading-button";
import { useLoadingDelay } from "~/hooks/use-loading-delay";

import pulseLogo from "./3D Smile Pulse_popup.jpg";
import eventInvitation from "./event-invitation.jpg";
import { PriceListUpdateDialog } from "./price-list-update-dialog";
import { RedesignWelcomeDialog } from "./redesign-welcome-dialog";
import telegramChannelLogo from "./telegram-channel-logo.svg";
import {
  ToastCloseIntent,
  ToastTypeTag,
  useCloseToastMutation,
  useGetToastsQuery,
} from "./toasts-api";

const TOAST_TIMEOUT_MS = 1500;

export function AppToaster() {
  const user = useRequiredUser();
  const toastsQuery = useGetToastsQuery();
  const [displayRedesignWelcomeDialog, setDisplayRedesignWelcomeDialog] = useState(false);
  const [displayPriceListUpdateDialog, setDisplayPriceListUpdateDialog] = useState(false);

  const isYandexMetrikaToastShown = useRef(false);
  const isTelegramChannelToastShown = useRef(false);
  const isEventInvitationToastShown = useRef(false);
  const isPulseSurveyToastShown = useRef(false);

  useEffect(() => {
    const toastTypesForDisplay = toastsQuery.data;
    if (!toastTypesForDisplay) {
      return;
    }

    const toasts: React.ComponentType<ToastProps>[] = [];

    if (!user.privileges.accepted_yandex_metrika && !isYandexMetrikaToastShown.current) {
      isYandexMetrikaToastShown.current = true;
      toasts.push(YandexMetrikaToast);
    }

    if (
      toastTypesForDisplay.includes(ToastTypeTag.TELEGRAM_CHANNEL) &&
      !isTelegramChannelToastShown.current
    ) {
      isTelegramChannelToastShown.current = true;
      toasts.push(TelegramChannelToast);
    }

    if (
      toastTypesForDisplay.includes(ToastTypeTag.EVENT_INVITATION_I_AM_DOCTOR) &&
      !isEventInvitationToastShown.current
    ) {
      isEventInvitationToastShown.current = true;
      toasts.push(EventInvitationToast);
    }

    if (
      toastTypesForDisplay.includes(ToastTypeTag.PULSE_SURVEY_2025) &&
      !isPulseSurveyToastShown.current
    ) {
      isPulseSurveyToastShown.current = true;
      toasts.push(PulseSurveyToast);
    }

    let additionalIndex = 0;

    // The welcome copy applies only to the redesign interface.
    if (
      featureFlags.redesignV1 &&
      toastTypesForDisplay.includes(ToastTypeTag.REDESIGN_V1_WELCOME)
    ) {
      setTimeout(
        () => setDisplayRedesignWelcomeDialog(true),
        TOAST_TIMEOUT_MS * (additionalIndex + 1),
      );
      additionalIndex++;
    }

    if (toastTypesForDisplay.includes(ToastTypeTag.PRICE_LIST_UPDATE_JUNE_2025)) {
      setTimeout(
        () => setDisplayPriceListUpdateDialog(true),
        TOAST_TIMEOUT_MS * (additionalIndex + 1),
      );
      additionalIndex++;
    }

    for (const [index, ToastComponent] of toasts.entries()) {
      setTimeout(
        () => persistentToast(ToastComponent),
        TOAST_TIMEOUT_MS * (index + additionalIndex + 1),
      );
    }
  }, [user.privileges.accepted_yandex_metrika, toastsQuery.data]);

  return (
    <>
      <RedesignWelcomeDialog
        isOpen={
          featureFlags.redesignV1 &&
          (toastsQuery.data ?? []).includes(ToastTypeTag.REDESIGN_V1_WELCOME) &&
          displayRedesignWelcomeDialog
        }
      />
      <PriceListUpdateDialog
        isOpen={
          (toastsQuery.data ?? []).includes(ToastTypeTag.PRICE_LIST_UPDATE_JUNE_2025) &&
          displayPriceListUpdateDialog
        }
      />
    </>
  );
}

export function SonnerToasterHost() {
  return <Toaster closeButton gap={12} mobileOffset={{ left: 4, bottom: 8 }} style={{ zIndex: ZIndex.TOASTER }} />;
}

function YandexMetrikaToast({ toastId }: ToastProps) {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const displayLoading = useLoadingDelay(isSubmitting, { delay: 200 });

  async function handleClick() {
    if (isSubmitting) {
      return;
    }
    setIsSubmitting(true);
    try {
      const response = await fetchWithAuth(`${process.env.API}/yandex-metrika`, {
        method: "POST",
      });
      if (!response.ok) {
        throw new Error(
          `Error occurred trying to accept yandex metrika "${response.status} ${response.statusText}"`,
        );
      }
    } catch (err) {
      remoteLog(err, "yandex_metrika_toast");
    } finally {
      sonnerToast.dismiss(toastId);
    }
  }

  return (
    <Toast>
      <p className="tw-m-0">
        <FormattedMessage id="toasts.yandex_metrika.text" />
        <span className="tw-block tw-h-1" />
        <Link to="/pages/yandex-metrika" className="tw-underline tw-underline-offset-4">
          <FormattedMessage id="learn_more" />
        </Link>
      </p>

      <ToastAction onClick={handleClick} isLoading={displayLoading}>
        OK
      </ToastAction>
    </Toast>
  );
}

function TelegramChannelToast({ toastId }: ToastProps) {
  const { closeToast } = useCloseToast({ toastId, tag: ToastTypeTag.TELEGRAM_CHANNEL });

  return (
    <Toast
      className="tw-px-5 tw-py-4 max-[600px]:tw-flex-wrap"
      onClose={() => closeToast({ intent: ToastCloseIntent.CLOSE })}
    >
      <div className="tw-flex tw-items-center tw-gap-5 tw-text-[13px]">
        <img width={70} src={telegramChannelLogo} style={{ width: 70, height: 70 }} alt="" />

        <p className="tw-m-0">
          <FormattedMessage id="toasts.telegram.text" /> <br className="max-[600px]:tw-hidden" /> 3D
          Smile LIVE
        </p>
      </div>

      <ToastAction asChild>
        <a
          href="https://t.me/smile_aligners_lab"
          target="_blank"
          rel="noopener noreferrer"
          className="max-[600px]:tw-w-full"
          onClick={() => closeToast({ intent: ToastCloseIntent.ACTION })}
        >
          <FormattedMessage id="toasts.telegram.goto" />
        </a>
      </ToastAction>
    </Toast>
  );
}

function EventInvitationToast({ toastId }: ToastProps) {
  const { closeToast } = useCloseToast({ toastId, tag: ToastTypeTag.EVENT_INVITATION_I_AM_DOCTOR });

  return (
    <Toast className="tw-px-5 tw-py-4 max-[600px]:tw-flex-wrap">
      <div className="tw-flex tw-items-center tw-gap-5 tw-text-[13px]">
        <img
          width={70}
          src={eventInvitation}
          className="tw-rounded-full"
          style={{ width: 70, height: 70 }}
          alt=""
        />

        <p className="tw-m-0">
          Приглашаем на конференцию 3D Smile
          <br /> {`"Я врач!" 4 апреля 2025 в Москве.`}
          <br /> ОРТОДОНТИЯ. ЭСТЕТИКА. БИЗНЕС
        </p>
      </div>

      <ToastAction asChild>
        <a
          href="https://3d-smile.ru/konferenciya-3d-smile-2025"
          target="_blank"
          rel="noopener noreferrer"
          className="max-[600px]:tw-w-full"
          onClick={() => closeToast({ intent: ToastCloseIntent.ACTION })}
        >
          <FormattedMessage id="learn_more" />
        </a>
      </ToastAction>
    </Toast>
  );
}

function PulseSurveyToast({ toastId }: ToastProps) {
  const { closeToast } = useCloseToast({ toastId, tag: ToastTypeTag.PULSE_SURVEY_2025 });

  return (
    <Toast
      className="tw-px-5 tw-py-4 max-[600px]:tw-flex-wrap"
      onClose={() => closeToast({ intent: ToastCloseIntent.CLOSE })}
    >
      <div className="tw-flex tw-items-center tw-gap-5 tw-text-[13px]">
        <img
          width={70}
          src={pulseLogo}
          className="tw-rounded-full"
          style={{ width: 70, height: 70 }}
          alt=""
        />

        <div className="tw-m-0">
          <p className="tw-m-0">Уважаемый доктор!</p>
          <div className="tw-h-1.5" />
          <p className="tw-m-0">
            Ваша экспертиза – наш главный ориентир! Пожалуйста, пройдите опрос 3D Smile Pulse –
            вместе мы создадим идеальное решение для Вашей практики.
          </p>
        </div>
      </div>

      <ToastAction asChild>
        <a
          href="https://forms.yandex.ru/cloud/68906be7eb614622f0ca93cd"
          target="_blank"
          rel="noopener noreferrer"
          className="max-[600px]:tw-w-full"
          onClick={() => closeToast({ intent: ToastCloseIntent.ACTION })}
        >
          Пройти
        </a>
      </ToastAction>
    </Toast>
  );
}

function Toast({
  children,
  className,
  onClose,
}: React.PropsWithChildren<{ className?: string; onClose?(): void }>) {
  return (
    <div
      className={clsx(
        "tw-relative tw-flex tw-items-center tw-justify-between tw-gap-4 tw-rounded-xl tw-border",
        "tw-border-gray-300 tw-bg-white tw-p-6 tw-leading-8 tw-text-brand-black tw-shadow-lg",
        "min-[600px]:tw-min-w-[550px]",
        className,
      )}
    >
      {children}

      {onClose ? (
        <button
          type="button"
          className={clsx(
            "tw-absolute tw-right-1 tw-top-1 tw-h-5 tw-w-5 tw-border-0 tw-bg-transparent tw-p-0 tw-text-brand-gray",
            "tw-inline-flex tw-items-center tw-justify-center tw-transition-colors hover:tw-text-gray-800",
          )}
          onClick={onClose}
        >
          <Cross2Icon className="tw-h-5 tw-w-5" />

          <span className="tw-sr-only">
            <FormattedMessage id="alert.close" />
          </span>
        </button>
      ) : null}
    </div>
  );
}

function ToastAction({ isLoading, ...props }: Partial<LoadingButtonProps>) {
  const buttonProps = {
    ...props,
    variant: "primary",
    rounded: "extra",
    size: "1.5xl",
  } satisfies ButtonProps;

  if (typeof isLoading == "boolean") {
    return <LoadingButton {...buttonProps} isLoading={isLoading} />;
  }

  return <Button {...buttonProps} />;
}

type ToastProps = {
  toastId: string | number;
};

function persistentToast(Component: React.ComponentType<ToastProps>) {
  return sonnerToast.custom((id) => <Component toastId={id} />, {
    dismissible: false,
    duration: Infinity,
  });
}

export function useCloseToast({
  tag,
  toastId,
}: {
  tag: ToastTypeTag;
  toastId?: ToastProps["toastId"];
}) {
  const [closeToastMutation, { isLoading, originalArgs }] = useCloseToastMutation();
  const isCloseLoading = isLoading && originalArgs?.intent == ToastCloseIntent.CLOSE;

  async function closeToast({ intent }: { intent: ToastCloseIntent }) {
    if (isLoading) {
      return;
    }
    try {
      await closeToastMutation({ tag, intent }).unwrap();
    } finally {
      if (toastId != undefined) {
        sonnerToast.dismiss(toastId);
      }
    }
  }

  return { closeToast, isLoading, isCloseLoading };
}
